import { useCallback, useMemo } from "react";
import { Badge, Button, Group, Table, Text, Tooltip } from "@mantine/core";
import dayjs from "dayjs";
import { SendOut, SendoutStatus } from "../../services/sendOuts.service";

interface TissueCypherSendOutRowProps {
  sendOut: SendOut;
  onView: (sendOut: SendOut) => void;
  onShip: (sendOut: SendOut) => void;
}

const STATUS_PRESENTATION: Record<
  SendoutStatus,
  { color: string; label: string }
> = {
  [SendoutStatus.PENDING_APPROVAL]: {
    color: "yellow",
    label: "Pending approval",
  },
  [SendoutStatus.REJECTED]: { color: "red", label: "Rejected" },
  [SendoutStatus.CANCELLED]: { color: "gray", label: "Cancelled" },
  [SendoutStatus.PENDING_SHIPPING]: { color: "yellow", label: "Ready to ship" },
  [SendoutStatus.SHIPPED]: { color: "blue", label: "Shipped" },
  [SendoutStatus.RECEIVED_PARTIALLY]: {
    color: "orange",
    label: "Partially returned",
  },
  [SendoutStatus.RECEIVED]: { color: "green", label: "Report returned" },
};

export function TissueCypherSendOutRow({
  sendOut,
  onView,
  onShip,
}: TissueCypherSendOutRowProps) {
  const handleView = useCallback(() => onView(sendOut), [onView, sendOut]);
  const handleShip = useCallback(() => onShip(sendOut), [onShip, sendOut]);
  const status = useMemo(
    () => STATUS_PRESENTATION[sendOut.status],
    [sendOut.status]
  );
  const slideCount = useMemo(
    () => sendOut.slidesSent?.length ?? 0,
    [sendOut.slidesSent]
  );

  return (
    <Table.Tr>
      <Table.Td>
        <Text fw={600}>{sendOut.case?.caseNumber ?? "N/A"}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">Castle Biosciences</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{slideCount} slides</Text>
      </Table.Td>
      <Table.Td>
        <Badge color={status.color} variant="light">
          {status.label}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Tooltip label={dayjs(sendOut.createdAt).format("MMM D, YYYY h:mm A")}>
          <Text size="sm">
            {dayjs(sendOut.createdAt).format("MMM D, YYYY")}
          </Text>
        </Tooltip>
      </Table.Td>
      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          {sendOut.status === SendoutStatus.PENDING_SHIPPING && (
            <Button h={44} onClick={handleShip}>
              Ship to Castle
            </Button>
          )}
          <Button variant="subtle" h={44} onClick={handleView}>
            View send-out
          </Button>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}
